import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  FormContainer,
  FormSection,
  FloatingField,
  InputText,
  Select,
} from '../../components/aip';
import { createAdminUser } from '../../services/manageNewApi';

// 権限選択肢
const ROLE_OPTIONS = [
  { value: '0', label: '一般' },
  { value: '1', label: '管理者' },
];

/**
 * ユーザ新規作成ページ
 */
export const AdminUserCreatePage: React.FC = () => {
  const navigate = useNavigate();

  // データ状態
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フォーム状態
  const [adminNumber, setAdminNumber] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminEmailSub, setAdminEmailSub] = useState('');
  const [isAdmin, setIsAdmin] = useState('0');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // 保存処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // バリデーション
    if (!adminName.trim()) {
      setError('ユーザ名は必須です');
      return;
    }
    if (!adminEmail.trim()) {
      setError('メールアドレスは必須です');
      return;
    }
    if (!password) {
      setError('パスワードは必須です');
      return;
    }
    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }
    if (password !== passwordConfirm) {
      setError('パスワードが一致しません');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await createAdminUser({
        admin_number: adminNumber.trim() || undefined,
        admin_name: adminName.trim(),
        admin_email: adminEmail.trim(),
        admin_email_sub: adminEmailSub.trim() || undefined,
        is_admin: parseInt(isAdmin, 10),
        password,
      });

      navigate('/meeting/manage-new/admin-users');
    } catch (err) {
      setError(err instanceof Error ? err.message : '作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-red50)',
            color: 'var(--color-alert)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <FormContainer onSubmit={handleSubmit}>
        <FormSection title="基本情報" layout="vertical">
          <FloatingField label="ユーザ名" htmlFor="admin_name" required>
            <InputText
              id="admin_name"
              name="admin_name"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              placeholder="ユーザ名"
            />
          </FloatingField>

          <FloatingField label="社員番号" htmlFor="admin_number">
            <InputText
              id="admin_number"
              name="admin_number"
              value={adminNumber}
              onChange={(e) => setAdminNumber(e.target.value)}
              placeholder="社員番号"
            />
          </FloatingField>

          <FloatingField label="メールアドレス（ログインID）" htmlFor="admin_email" required>
            <InputText
              id="admin_email"
              name="admin_email"
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="メールアドレス"
            />
          </FloatingField>

          <FloatingField label="サブメールアドレス" htmlFor="admin_email_sub">
            <InputText
              id="admin_email_sub"
              name="admin_email_sub"
              type="email"
              value={adminEmailSub}
              onChange={(e) => setAdminEmailSub(e.target.value)}
              placeholder="サブメールアドレス"
            />
          </FloatingField>

          <FloatingField label="権限" htmlFor="is_admin" required>
            <Select
              id="is_admin"
              name="is_admin"
              value={isAdmin}
              onChange={(e) => setIsAdmin(e.target.value)}
              options={ROLE_OPTIONS}
            />
          </FloatingField>
        </FormSection>

        <FormSection title="パスワード">
          <FloatingField label="パスワード" htmlFor="password" required>
            <InputText
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8文字以上"
            />
          </FloatingField>

          <FloatingField label="パスワード（確認）" htmlFor="password_confirm" required>
            <InputText
              id="password_confirm"
              name="password_confirm"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              placeholder="パスワード再入力"
            />
          </FloatingField>
        </FormSection>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button
            buttonType="outline"
            size="md"
            type="button"
            onClick={() => navigate('/meeting/manage-new/admin-users')}
            disabled={saving}
          >
            キャンセル
          </Button>
          <Button buttonType="solid-fill" size="md" type="submit" disabled={saving}>
            {saving ? '作成中...' : '作成'}
          </Button>
        </div>
      </FormContainer>
    </>
  );
};

export default AdminUserCreatePage;

